import * as React from "react";
import cs from "classnames";
import * as cornerstone from "cornerstone-core";
import i18n from "i18next";
import { Component, CSSProperties, RefObject } from "react";
import CancelableAxiosInstance from "../../api/CancelableAxiosInstance";
import { AiResult, Marker } from "../../api/EvalWorkflowApi";
import { ParamType } from "../../api/GrammarApi";
import { ImageResult } from "../../api/ImageResultApi";
import { NegativeRegion, Region, RegionGroup, RegionType, Series } from "../../api/StudyApi";
import ImageService from "../../service/ImageService";
import arr from "../../util/ArrayDecorator";
import CornerstoneUtil from "../../util/CornerstoneUtil";
import { AuthContext, AuthManager } from "../auth/AuthContext";
import { AiDisplayPreferences } from "./AiDisplayPreferences";
import { DisplayMode, ViewerControl } from "./DisplayMode";
import MarkerMenu, { AddMarkerMenu } from "./MarkerMenu";
import { Point } from "./Point";
import RegionMenu from "./RegionMenu";
import SeriesNav from "./SeriesNav/SeriesNav";
import ConfidenceControl from "./Tools/ConfidenceControl";
import InvertControl from "./Tools/InvertControl";
import ViewerToolIcon from "./Tools/ViewerToolIcon";
import Viewport, { AnnotationToolMode } from "./Viewport";
import ViewportImage from "./ViewportImage";
import "./viewer.scss";

interface ViewerProps {
  study: {
    id: string;
    series: Array<Series>;
    aiResult?: AiResult;
    obsTypes?: string[];
  };

  observations?: Array<RegionGroup>;

  images: Array<ViewportImage>;
  imageResults?: Array<ImageResult>;
  nextImagePaths?: Array<string>;

  maxSelectableImageIdx?: number;

  onImageTagsChanged?(imageId: string, tags: Array<string>): void;

  onImageLoaded?(): void;

  onImageSelected?(viewportImage: ViewportImage): void;

  imageTags?: string[];
  display?: DisplayMode;
  aiDisplayPreferences: AiDisplayPreferences;

  onAiRegionActivation?(): void;

  aside?: { (): JSX.Element };
  underSeries?: { (): JSX.Element };
  actions?: { (): JSX.Element };
  authManager: AuthManager;
  annotationToolMode?: AnnotationToolMode;
  paramTypesByObsTypes?: Record<string, ParamType[]>;

  onKey?(evt: React.KeyboardEvent): void;

  // Markers related
  saveMarker?: (marker: Marker, viewportImage: ViewportImage) => void;

  deleteMarker?(marker: Marker): void;

  // regions related
  saveRegion?(region: Region, fromCopy?: boolean): void;

  deleteRegion?(region: Region): void;

  addAIRegionToNegative?(region: Region): void;

  onMarkerIO?: (marker: Marker, isIn: boolean, pageRect: Point) => void;
}

interface ViewerState {
  tool: string;
  inverted: boolean;
  viewportSelectedIndex: number;
  viewportImages: Array<ViewportImage>;
  selectedRegion?: Region | NegativeRegion;
  annotationMenuStyle?: CSSProperties;
  selectedMarker?: Marker;
  aiResult?: AiResult;
  display: DisplayMode;
  aiTimer?: number;
  addingMarker?: Marker;
}

export default class Viewer extends Component<ViewerProps, ViewerState> {
  maxViewport = 1;

  private viewport: React.RefObject<HTMLDivElement>;

  private readonly client: CancelableAxiosInstance;
  private readonly imageLoaderScheme: string;

  private aiInterval: () => void;
  private intervalId: number;
  private selectedRegion?: Region;

  private divSeriesAndViewportWrapper: RefObject<HTMLDivElement>;
  private imageService: ImageService;

  /**
   * Be careful that when clicking on viewports wrapper elements, a listener is registered in body element. That means only one viewer component is authorized in the same page
   * @param props
   */
  constructor(props: ViewerProps) {
    super(props);

    this.client = this.props.authManager.cancelableClient();

    this.imageLoaderScheme = CornerstoneUtil.setupLoader(this.client);
    this.imageService = new ImageService(this.imageLoaderScheme);

    this.viewport = React.createRef<HTMLDivElement>();

    this.state = {
      tool: "Pan",
      inverted: false,
      viewportSelectedIndex: 0,
      aiResult: props.aiDisplayPreferences.computeStudyResult(props.study),
      viewportImages: props.images.slice(0, this.maxViewport),
      display: props.display,
      aiTimer: props.display.timerBeforeAiEnabled,
      addingMarker: null,
    };

    this.divSeriesAndViewportWrapper = React.createRef();

    if (props.nextImagePaths) {
      props.nextImagePaths.forEach((path) => {
        this.imageService.scheduleImageLoading(path);
      });
    }
  }

  componentDidMount(): void {
    if (this.props?.images) {
      this.props.images.forEach((img) => {
        this.imageService.scheduleImageLoading(img.path);
      });
    }

    this.setupAiTimer();
    this.focusOnSeriesAndViewport();
  }

  UNSAFE_componentWillReceiveProps(nextProps: ViewerProps) {
    if (!arr(this.props.nextImagePaths).containsExactlySameElementsInAnyOrder(nextProps.nextImagePaths)) {
      nextProps.nextImagePaths.forEach((imgPath) => {
        this.imageService.scheduleImageLoading(imgPath);
      });
    }

    // Get the previous selected VP idx

    if (!this.props.study || this.props.study.id !== nextProps.study.id) {
      nextProps.images.forEach((img) => {
        this.imageService.scheduleImageLoading(img.path);
      });

      const selectedImg = nextProps.images?.find((imgElt) => imgElt.selected);

      this.stopAiTimer();
      this.setupAiTimer();

      // New study
      this.setState(
        {
          viewportSelectedIndex: 0,
          inverted: false,
          viewportImages: selectedImg ? [selectedImg] : nextProps.images.slice(0, this.maxViewport),
          display: this.state.display.onNextStudy(),
          aiResult: nextProps.aiDisplayPreferences.computeStudyResult(nextProps.study),
          aiTimer: this.state.display.timerBeforeAiEnabled,
        },
        () => {
          this.focusOnSeriesAndViewport();
        }
      );
    } else {
      // Is an obs need to show region
      const obs = nextProps.observations?.find((obs) => obs.showRegion);

      if (obs) {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { showRegion, ...updatedObs } = obs;

        this.optimizeViewportsFor(updatedObs);
      } else {
        // Update images in viewports
        this.setState(({ viewportImages }) => ({
          viewportImages: viewportImages
            .map((imageElement) => nextProps.images.find((iwr) => iwr.is(imageElement)))
            .filter(Boolean),
        }));
      }
    }
  }

  componentDidUpdate() {
    const { selectedMarker: currentSelectedMarker, viewportImages, viewportSelectedIndex } = this.state;

    const selectedImage = this.props.images.find((image) => image.markers.some((marker) => marker._hoveredFrom));

    if (selectedImage && selectedImage.path === viewportImages[viewportSelectedIndex].path) {
      const selectedMarker = selectedImage.markers.find((marker) => marker._hoveredFrom);

      if (selectedMarker !== currentSelectedMarker) {
        const coords = cornerstone.pixelToCanvas(this.viewport.current, selectedMarker);
        this.setState({
          selectedMarker,
          annotationMenuStyle: { top: coords.y + 30, left: coords.x + 165 },
        });
      }
    } else if (currentSelectedMarker) {
      this.setState({
        selectedMarker: null,
        annotationMenuStyle: null,
      });
    }
  }

  private focusOnSeriesAndViewport() {
    this.divSeriesAndViewportWrapper.current.focus();
  }

  setupAiTimer() {
    if (this.state.display.timerBeforeAiEnabled === 0) {
      return;
    }
    this.aiInterval = () => {
      this.intervalId = window.setInterval(() => {
        this.setState(
          (prevState) => ({ aiTimer: prevState.aiTimer - 1 }),
          () => {
            if (this.state.aiTimer === 0) {
              this.stopAiTimer();
              this.setState((p) => ({ display: p.display.enableAiControl() }));
            }
          }
        );
      }, 1000);
    };
  }

  stopAiTimer() {
    this.intervalId && clearInterval(this.intervalId);
  }

  componentWillUnmount() {
    Viewer.removeEventListenerAuthorizeTextSelection();
    this.stopAiTimer();
  }

  static removeEventListenerAuthorizeTextSelection() {
    document.body.removeEventListener("mouseup", Viewer.authorizeTextSelectionAndAddEventListenerOnMouseUp);
  }

  private preventTextSelectionBetweenMouseDownAndUp() {
    // When user does a mouse down on the viewports element, he can't select any text
    Viewer.preventTextSelection();
    // Wherever user does a mouse up on the window, it should let user select text again
    document.body.addEventListener("mouseup", Viewer.authorizeTextSelectionAndAddEventListenerOnMouseUp);
  }

  static preventTextSelection() {
    document.body.classList.add("unselectable");
  }

  static authorizeTextSelectionAndAddEventListenerOnMouseUp() {
    document.body.classList.remove("unselectable");
    Viewer.removeEventListenerAuthorizeTextSelection();
  }

  activateTool = (toolName: string) => {
    this.setState({ tool: toolName });
  };

  toggleAiRegions(): void {
    this.setState(
      (prevState) => ({
        display: prevState.display.toggleAi(),
      }),
      () => this.onAiRegionActivation()
    );
  }

  private onAiRegionActivation() {
    this.state.display.ai.annotationDisplayed && this.props.onAiRegionActivation?.();
  }

  togglePasses(): void {
    this.setState((prevState) => ({
      display: prevState.display.togglePasses(),
    }));
  }

  toggleObs(): void {
    this.setState((prevState) => ({
      display: prevState.display.toggleObs(),
    }));
  }

  toggleAllAnnotations(): void {
    this.setState(
      (prevState) => ({
        display: prevState.display.toggleAll(),
      }),
      () => this.onAiRegionActivation()
    );
  }

  toggleCrossCursor(): void {
    this.setState((prevState) => ({
      display: prevState.display.toggleCursor(),
    }));
  }

  optimizeViewportsFor(observation: RegionGroup) {
    if (observation?.regions?.length > 0) {
      const imageIds = observation.regions.reduce((acc: string[], region) => {
        if (!acc.includes(region.imageId)) {
          acc.push(region.imageId);
        }
        return acc;
      }, []);

      let images = this.props.images.filter((imgElt) => imageIds.includes(imgElt.image.id));

      if (imageIds.length > this.maxViewport) {
        images = images.slice(0, this.maxViewport);
      }

      this.setState({ viewportImages: images });
    }
  }

  // -------- Handlers

  onClickViewport(idx: number) {
    this.setState({ viewportSelectedIndex: idx }, () => this.onSelectImage(this.state.viewportImages[idx]));
  }

  saveMarker(marker: Marker, idx = -1) {
    if (idx < 0) throw new Error("Corresponding viewport unavailable");

    // New one
    const imageElement = this.state.viewportImages[idx];

    if (!marker.imageId) {
      marker.imageId = imageElement.id;
    }

    if (this.props.saveMarker) {
      this.props.saveMarker(marker, imageElement);
    }
  }

  saveRegion(region: Region, idx = -1) {
    if (!region.imageId) {
      if (idx < 0) throw new Error("Corresponding viewport unavailable");

      // New one
      const imageElement = this.state.viewportImages[idx];
      region.imageId = imageElement.id;
    }

    if (this.props.saveRegion) {
      this.props.saveRegion(region);
    }
  }

  deleteRegion(region: Region) {
    if (this.props.deleteRegion) {
      this.props.deleteRegion(region);
    }
    this.closeAnnotationMenu();
  }

  deleteMarker = (marker: Marker) => {
    if (this.props.deleteMarker) {
      this.props.deleteMarker(marker);
    }
    this.closeAnnotationMenu();
  };

  onRegionHandling() {
    this.setState({ selectedRegion: null });
  }

  onRegionIO(region: Region, isIn: boolean, pageRect: Point) {
    if (isIn) {
      // Display region menu
      this.setState(({ viewportImages }) => ({
        selectedRegion: region,
        annotationMenuStyle: { top: pageRect.y, left: pageRect.x },
        viewportImages: viewportImages.map((img) => img.activateRegion(region)),
      }));
    } else {
      this.closeAnnotationMenu();
    }
  }

  private closeAnnotationMenu() {
    this.resetAnnotationMenu();
  }

  resetAnnotationMenu() {
    this.setState(({ viewportImages }) => ({
      selectedRegion: null,
      selectedMarker: null,
      annotationMenuStyle: null,
      addingMarker: null,
      viewportImages: viewportImages.map((img) => img.deactivate()),
    }));
  }

  isAnnotationMenuOpen() {
    return this.state.annotationMenuStyle;
  }

  onMarkerHandling() {
    this.setState({ selectedMarker: null });
  }

  onMarkerIo = (marker: Marker, isIn: boolean, pageRect: Point) => {
    if (isIn) {
      this.setState({
        selectedMarker: { ...marker },
        annotationMenuStyle: { top: pageRect.y, left: pageRect.x + 10 },
      });
    } else {
      this.setState({ selectedMarker: null });
      this.closeAnnotationMenu();
    }

    this.props.onMarkerIO?.(marker, isIn, pageRect);
  };

  onSelectImage(viewportImage: ViewportImage) {
    this.setState({
      viewportImages: this.props.images
        .map((imageElement) => {
          if (imageElement.path !== viewportImage.path) {
            return imageElement.deselect();
          }
          return viewportImage.select();
        })
        .filter((image) => image.selected),
    });

    if (this.props.onImageSelected) {
      this.props.onImageSelected(viewportImage);
    }
  }

  private selectPreviousImage() {
    const currentIdx = this.retrieveCurrentImageIdx();

    if (currentIdx <= 0) {
      return;
    }

    this.onSelectImage(this.props.images[currentIdx - 1]);
  }

  private retrieveCurrentImageIdx() {
    return this.props.images?.findIndex((img) => !!img.selected) ?? -1;
  }

  private selectNextImage() {
    const currentIdx = this.retrieveCurrentImageIdx();

    if (
      currentIdx >= this.props.images.length - 1 ||
      (this.props.maxSelectableImageIdx && currentIdx >= this.props.maxSelectableImageIdx - 1)
    ) {
      return;
    }

    this.onSelectImage(this.props.images[currentIdx + 1]);
  }

  private filterAiRegions(e: React.ChangeEvent<HTMLInputElement>) {
    const confidence = Number(e.target.value);
    this.setState((prevState) => {
      return {
        display: prevState.display.updateConfidence(confidence),
      };
    });
  }

  private onImageTagsChanged(imageId: string, tags: Array<string>) {
    this.props.onImageTagsChanged(imageId, tags);
  }

  private onKeyDown(evt: React.KeyboardEvent) {
    const { display } = this.state;

    if (evt.key === "a") {
      this.toggleAllAnnotations();
    }

    if (evt.key === "z") {
      this.toggleObs();
    }

    if (evt.key === "e" && display.ai.controlEnabled) {
      this.toggleAiRegions();
    }

    if (evt.key === "r" && display.pass.controlEnabled) {
      this.togglePasses();
    }

    if (evt.key === "q" || evt.key === "ArrowUp") {
      this.selectPreviousImage();
    }

    if (evt.key === "w" || evt.key === "ArrowDown") {
      this.selectNextImage();
    }

    if (evt.key === "t" && display.cursorControl.controlEnabled) {
      this.toggleCrossCursor();
    }

    if (evt.key === "c" && this.selectedRegion) {
      this.copyOrReplaceSelectedRegion();
    }

    if (evt.key === "n" && this.selectedRegion) {
      this.addNegativeForSelectedAiRegion();
    }

    if (evt.key === "x") {
      this.invertContrast();
    }

    this.props.onKey?.(evt);
  }

  invertContrast = () => {
    this.setState(({ inverted }) => ({ inverted: !inverted }));
  };

  private onRegionMenuKeyDown(evt: React.KeyboardEvent) {
    this.onKeyDown(evt);
  }

  private copyOrReplaceSelectedRegion() {
    if (this.props.saveRegion) {
      let fromAiCopy = false;

      if (
        (this.selectedRegion._type === RegionType.NEGATIVE ||
          this.selectedRegion._type === RegionType.OBSERVATION ||
          this.selectedRegion._type === RegionType.OBSERVATION_GT) &&
        this.props.deleteRegion
      ) {
        this.props.deleteRegion(this.selectedRegion);
        this.closeAnnotationMenu();
      } else {
        fromAiCopy = true;
      }

      this.props.saveRegion(
        {
          x: this.selectedRegion.x,
          y: this.selectedRegion.y,
          width: this.selectedRegion.width,
          height: this.selectedRegion.height,
          imageId: this.selectedRegion.imageId,
        },
        fromAiCopy
      );

      this.selectedRegion = null;
    }
  }

  private addNegativeForSelectedAiRegion() {
    if (
      this.state.display.ai.annotationDisplayed &&
      this.props.addAIRegionToNegative &&
      this.selectedRegion._type === RegionType.AI
    ) {
      this.props.addAIRegionToNegative({
        x: this.selectedRegion.x,
        y: this.selectedRegion.y,
        obsType: this.selectedRegion.obsType,
        width: this.selectedRegion.width,
        height: this.selectedRegion.height,
        imageId: this.selectedRegion.imageId,
      });
      this.selectedRegion = null;
    }
  }

  private onSelectedRegion(region: Region) {
    this.selectedRegion = region;

    if (!this.isAnnotationMenuOpen()) {
      this.focusOnSeriesAndViewport();
    }
  }

  private onImageLoaded() {
    if (this.aiInterval) {
      this.aiInterval();
      this.aiInterval = null;
    }
    this.props.onImageLoaded?.();
  }

  onMarkerAdding = ({ x, y }: Point, pagePosition: Point) => {
    // This prevents from an infinite re-render while marker is being added
    if (this.state.addingMarker?.x === x && this.state.addingMarker?.y === y) {
      return;
    }

    const { study, paramTypesByObsTypes } = this.props;

    const defaultObsType = study.obsTypes?.[0] || "FRACT";
    const paramTypes = paramTypesByObsTypes[defaultObsType] || [];
    const markerParams = paramTypes
      .filter((paramType) => paramType.defaultOpt)
      .reduce(
        (defaultParams, paramType) => ({
          ...defaultParams,
          [paramType.code]: paramType.defaultOpt,
        }),
        {}
      );

    this.setState(({ viewportImages, viewportSelectedIndex }) => ({
      addingMarker: {
        x,
        y,
        imageId: viewportImages[viewportSelectedIndex].image.id,
        _hoveredFrom: null,
        _label: "",
        obsType: "",
        params: markerParams,
      },
      annotationMenuStyle: {
        left: pagePosition.x,
        top: pagePosition.y,
      },
    }));
  };

  onConfirmMarker = (savedMarker: Marker) => {
    this.saveMarker(savedMarker, this.state.viewportSelectedIndex);
    this.setState({ addingMarker: null, annotationMenuStyle: null });
  };

  private displayControl(control: ViewerControl, callback: () => void, content: JSX.Element | string = control.name) {
    if (!control.controlDisplayed) {
      return;
    }

    return (
      <button
        title={control.tooltip}
        data-toggle="tooltip"
        className={"btn " + (control.annotationDisplayed ? "btn-primary" : "btn-secondary")}
        onClick={() => callback()}
        disabled={!control.controlEnabled}
      >
        {content}
      </button>
    );
  }

  private displayAllControl() {
    if (this.state.display.controlsDisplayed <= 1) {
      return;
    }

    return (
      <div className="btn-group-toggle" data-toggle="buttons">
        <button
          data-toggle="tooltip"
          title={this.state.display.allDisplayedButtonsChecked ? i18n.t("tool.hideAll") : i18n.t("tool.displayAll")}
          className={"btn " + (this.state.display.allDisplayedButtonsChecked ? "btn-primary" : "btn-secondary")}
          onClick={() => this.toggleAllAnnotations()}
          disabled={!this.state.display.allDisplayedButtonsEnabled}
        >
          All
        </button>
      </div>
    );
  }

  private displayCursorControl() {
    const { cursorControl } = this.state.display;

    if (!cursorControl.controlDisplayed || !cursorControl.controlEnabled) {
      return null;
    }

    return (
      <li>
        <div
          data-toggle="tooltip"
          title={
            cursorControl.annotationDisplayed ? i18n.t("tool.removeCrossCursor") : i18n.t("tool.displayCrossCursor")
          }
          className={cs("nav-item ", { active: !!cursorControl.annotationDisplayed })}
          onClick={() => this.toggleCrossCursor()}
        >
          <button className="nav-link unstyled-button fa fa-lg fa-crosshairs" onClick={() => this.activateTool(name)} />
        </div>
      </li>
    );
  }

  private displayControlsGroup() {
    const { display, aiTimer } = this.state;

    if (display.controlsDisplayed === 0) {
      return;
    }

    const aiButtonLabel = aiTimer && aiTimer > 0 ? `${display.ai.name}: ${aiTimer}` : display.ai.name;

    return (
      <li>
        {this.displayAllControl()}
        <div className="btn-group btn-group-toggle" data-toggle="buttons">
          {this.displayControl(display.obs, () => this.toggleObs())}
          {this.displayControl(display.ai, () => this.toggleAiRegions(), aiButtonLabel)}
          {this.displayControl(display.pass, () => this.togglePasses())}
        </div>
      </li>
    );
  }

  private navbar() {
    const tools = [
      { name: "Wwwc", icon: "fa-adjust", onClick: () => this.activateTool("Wwwc") },
      { name: "Zoom", icon: "fa-search", onClick: () => this.activateTool("Zoom") },
      { name: "Pan", icon: "fa-arrows", onClick: () => this.activateTool("Pan") },
    ];

    return (
      <nav className="navbar navbar-expand-lg navbar-dark bg-dark">
        <ul className="navbar-nav">
          {tools.map((tool) => (
            <ViewerToolIcon
              isActive={this.state.tool === tool.name}
              onClick={tool.onClick}
              icon={tool.icon}
              key={tool.name}
            />
          ))}
          <InvertControl onClick={this.invertContrast} inverted={this.state.inverted} />
          <ConfidenceControl
            displayed={this.state.display.confidence.controlDisplayed}
            confidenceThreshold={this.state.display.confidenceThreshold}
            onChange={(e) => this.filterAiRegions(e)}
          />

          {this.displayControlsGroup()}
          {this.displayCursorControl()}
        </ul>
      </nav>
    );
  }

  // ---------- Rendering

  render() {
    let onMarkerAdding: (pos: Point, pagePos: Point) => void | undefined = undefined;
    const { study, paramTypesByObsTypes } = this.props;
    const defaultObsTypes = this.props.study?.obsTypes?.[0] || "FRACT";
    if (study?.obsTypes?.length > 0 || (paramTypesByObsTypes && paramTypesByObsTypes[defaultObsTypes]?.length > 0)) {
      onMarkerAdding = this.onMarkerAdding;
    }

    return (
      <div className="viewer">
        {this.state.selectedRegion && (
          <div className="annotation-menu" style={this.state.annotationMenuStyle}>
            <RegionMenu
              region={this.state.selectedRegion}
              onChange={(r) => this.saveRegion(r)}
              onDelete={(r) => this.deleteRegion(r)}
              onMouseLeave={() => this.closeAnnotationMenu()}
              onKeyDown={(evt) => this.onRegionMenuKeyDown(evt)}
            />
          </div>
        )}

        {this.state.selectedMarker?._hoveredFrom && (
          <div className="annotation-menu" style={this.state.annotationMenuStyle}>
            <MarkerMenu
              marker={this.state.selectedMarker}
              paramTypesByObsTypes={this.props.paramTypesByObsTypes}
              obsTypes={this.props.study.obsTypes}
              onDelete={this.deleteMarker}
              onMouseLeave={() => this.closeAnnotationMenu()}
              onMarkerChange={(marker) =>
                this.props.saveMarker(marker, this.state.viewportImages[this.state.viewportSelectedIndex])
              }
            />
          </div>
        )}

        {this.state.addingMarker && (
          <div className="annotation-menu" style={this.state.annotationMenuStyle}>
            <AddMarkerMenu
              initialMarker={this.state.addingMarker}
              paramTypesByObsTypes={this.props.paramTypesByObsTypes}
              obsTypes={this.props.study.obsTypes}
              onMouseLeave={() => {
                this.deleteMarker(this.state.addingMarker);
                this.closeAnnotationMenu();
              }}
              onValidate={this.onConfirmMarker}
            />
          </div>
        )}

        <div
          ref={this.divSeriesAndViewportWrapper}
          className="series-and-viewport-wrapper"
          tabIndex={0}
          onKeyDown={(evt) => this.onKeyDown(evt)}
        >
          <SeriesNav
            series={this.props.study.series}
            images={this.props.images}
            onSelectImage={(img) => this.onSelectImage(img)}
            authManager={this.props.authManager}
            dspAiBadge={this.state.display.ai.annotationDisplayed}
            dspImageResults={this.props.aiDisplayPreferences.displayImageResults}
            underSeries={this.props.underSeries}
          />

          <div className="viewports-panel">
            {this.navbar()}

            <div
              className="viewports"
              style={{
                height: `${window.innerHeight - 50}px`,
                outline: "none",
              }}
              onMouseDown={() => this.preventTextSelectionBetweenMouseDownAndUp()}
            >
              <AuthContext.Consumer>
                {(authManager) =>
                  this.state.viewportImages.map((imageElement, index) => (
                    <Viewport
                      key={index}
                      innerRef={(element) => {
                        // @ts-ignore
                        this.viewport.current = element;
                      }}
                      annotationToolMode={this.props.annotationToolMode}
                      authManager={authManager}
                      index={index}
                      selected={index === this.state.viewportSelectedIndex}
                      inverted={this.state.inverted}
                      tool={this.state.tool}
                      imageElement={imageElement}
                      onClickViewport={this.onClickViewport.bind(this)}
                      saveMarker={(marker, idx) => this.saveMarker(marker, idx)}
                      onMarkerIO={this.onMarkerIo}
                      onMarkerHandling={() => this.onMarkerHandling()}
                      onMarkerAdding={onMarkerAdding}
                      saveRegion={(r, idx) => this.saveRegion(r, idx)}
                      onRegionIO={(r, isIn, point) => this.onRegionIO(r, isIn, point)}
                      onRegionHandling={() => this.onRegionHandling()}
                      onImageTagsChanged={(imageId, tags) => this.onImageTagsChanged(imageId, tags)}
                      imageTags={this.props.imageTags}
                      displayAiRegions={this.state.display.ai.annotationDisplayed}
                      displayAnnotations={this.state.display.obs.annotationDisplayed}
                      displayCursor={this.state.display.cursorControl.annotationDisplayed}
                      aiResult={this.state.aiResult}
                      aiDisplayPreferences={this.props.aiDisplayPreferences}
                      aiRegionMinConfidence={this.state.display.confidence.value}
                      displayPasses={this.state.display.pass.annotationDisplayed}
                      imageLoaderScheme={this.imageLoaderScheme}
                      onImageLoaded={() => this.onImageLoaded()}
                      onSelectedRegion={(region) => this.onSelectedRegion(region)}
                      client={this.client}
                    />
                  ))
                }
              </AuthContext.Consumer>
            </div>
          </div>
        </div>

        {this.props.actions && <div className="action-bar">{this.props.actions()}</div>}

        {this.props.aside && <div className="aside">{this.props.aside()}</div>}
      </div>
    );
  }
}
